/**
 * Editor management for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class EditorManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  // --- Model / Resource operations ---
  async saveModel(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/saveModel', { data });
  }

  async saveHtb(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/saveHtb', { data });
  }

  async uploadImage(file: File | Blob): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/editor/uploadImage', { data: formData, headers: {} });
  }

  async uploadDeviceStateModel(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/editor/uploadDeviceStateModel', { data: formData, params, headers: {} });
  }

  async coverModel(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/coverModel', { data });
  }

  async coverSimilarResources(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/coverSimilarResources', { data });
  }

  async batchUploadModel(files: (File | Blob)[], params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    for (const f of files) {
      formData.append('files', f);
    }
    return this.session.post('/api/cdp/editor/batchUploadModel', { data: formData, params, headers: {} });
  }

  async lock(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/lock', { data });
  }

  async unlock(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/unlock', { data });
  }

  async lockScene(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/lockScene', { data });
  }

  async unlockScene(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/unlockScene', { data });
  }

  async checkSceneVector(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/editor/checkSceneVector/${uuid}`);
  }

  async lockBusinessComponent(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/lockBusinessComponent', { data });
  }

  async unlockBusinessComponent(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/unlockBusinessComponent', { data });
  }

  async checkBusinessComponentVector(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/editor/checkBusinessComponentVector/${uuid}`);
  }

  async checkVector(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/editor/checkVector/${uuid}`);
  }

  // --- Upload material / resource ---
  async uploadMaterial(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/editor/uploadMaterial', { data: formData, params, headers: {} });
  }

  async uploadResource(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/editor/uploadResource', { data: formData, params, headers: {} });
  }

  async uploadSimilarResourcesZip(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/editor/uploadSimilarResourcesZip', { data: formData, params, headers: {} });
  }

  // --- Copy operations ---
  async copyDevicetypeModel(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/copyDevicetypeModel', { data });
  }

  async copySymbol(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/copySymbol', { data });
  }

  async shareProjectVector(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/shareProjectVector', { data });
  }

  async coverLoadingDisplay(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/coverLoadingDisplay', { data });
  }

  // --- Save / Get operations ---
  async save(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/save', { data });
  }

  async get(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/get', { data });
  }

  async getComponent(data: string | { uuid: string }): Promise<Record<string, any>> {
    const uuid = typeof data === 'string' ? data : data.uuid;
    if (!/^[A-Za-z0-9_-]+$/.test(uuid)) throw new Error('Invalid component UUID');
    return this.session.get(`/api/cdp/editor/getComponent/${uuid}`);
  }

  async saveScene(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/saveScene', { data });
  }

  async saveBusinessComponent(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/saveBusinessComponent', { data });
  }

  // --- Upload / Cover / Delete operations ---
  async upload(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/editor/upload', { data: formData, params, headers: {} });
  }

  async cover(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/cover', { data });
  }

  async delete(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/delete', { data });
  }

  async batchDelete(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/batchDelete', { data });
  }

  async rename(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/rename', { data });
  }

  // --- Background operations ---
  async uploadBackground(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/editor/uploadBackground', { data: formData, params, headers: {} });
  }

  async deleteBackground(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/deleteBackground', { data });
  }

  async batchDeleteBackground(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/batchDeleteBackground', { data });
  }

  async backgroundPage(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/editor/background/page', { params });
  }

  // --- List operations ---
  async listComponents(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/editor/components', { params });
  }

  async listPanels(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/editor/panel/list', { params });
  }

  async listVectors(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/editor/vector/list', { params });
  }

  async vectorPage(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/editor/vector/page', { params });
  }

  async deleteComponent(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/deleteComponent', { data });
  }

  // --- Scene copy ---
  async copyScene(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/copyScene', { data });
  }

  // --- Model operations ---
  async uploadModel(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/editor/uploadModel', { data: formData, params, headers: {} });
  }

  async getModelList(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/editor/list', { params });
  }

  async getModelFiles(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/editor/getModelFileById/${uuid}`);
  }

  async uploadLoadingDisplay(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/editor/uploadLoadingDisplay', { data: formData, params, headers: {} });
  }

  // --- 3D manual snapshot ---
  async save3DManualSnapshot(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/save3DManualSnapshot', { data });
  }

  async batchDelete3DManualSnapshot(uuids: string[]): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/batchDelete3DManualSnapshot', { data: { uuids } });
  }

  async saveNodeMaterial(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/saveNodeMaterial', { data });
  }

  // --- Struct definition ---
  async getStructList(page = 1, size = 20): Promise<Record<string, any>> {
    const params = {
      page,
      size,
      options: JSON.stringify({ where: { tenantId: this.session.tenantId }, order: ['updatedAt', 'DESC'] }),
      obtainedRelatedAttrs: true,
    };
    return this.session.get('/api/cdp_struct_def/page', { params });
  }

  // --- Vector operations ---
  async getVector(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp_vector/get/${uuid}`);
  }

  async createVector(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp_vector/extend', { data });
  }

  async checkVectorFile(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/cdp_vector/extend/existFile/${uuid}`);
  }

  async removeVector(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/editor/vector/delete/${uuid}`);
  }

  async batchCopyVector(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_vector/extend/batchCopyVector', { data });
  }
}
